(() => {
  class FiberCableTrayBundleBuilder {
    constructor(root) {
      this.root = root;
      this.parts = this.getParts();
      this.list = root.querySelector('[data-parts-list]');
      this.diagramWrap = root.querySelector('[data-svg-url]');
      this.svgContainer = root.querySelector('[data-svg-container]');
      this.modal = root.querySelector('[data-modal]');
      this.closeButton = root.querySelector('[data-close]');
      this.zoomInButton = root.querySelector('[data-zoom-in]');
      this.zoomOutButton = root.querySelector('[data-zoom-out]');
      this.zoomResetButton = root.querySelector('[data-zoom-reset]');
      this.quantity = root.querySelector('[data-quantity]');
      this.addButton = root.querySelector('[data-add-to-cart]');
      this.spinner = this.addButton?.querySelector('.loading__spinner');
      this.error = root.querySelector('[data-error]');
      this.activePart = null;
      this.selectedVariant = null;
      this.diagramZoom = 1;
      this.mobileMediaQuery = window.matchMedia?.('(max-width: 749px)');
      this.listMode = this.getListMode();
      this.svg = null;
      this.render();
      this.bindEvents();
      this.setDiagramZoom(1);
    }

    getParts() {
      const script = this.root.querySelector('[data-bundle-builder-parts]');
      if (!script) return [];

      try {
        return JSON.parse(script.textContent)
          .filter((part) => part.product && Array.isArray(part.product.variants))
          .sort((a, b) => Number(a.sortOrder || a.partNumber || 0) - Number(b.sortOrder || b.partNumber || 0));
      } catch (error) {
        return [];
      }
    }

    render() {
      this.renderList();
      this.loadSvg();
    }

    renderList() {
      if (!this.list) return;
      const isMobile = this.getListMode() === 'mobile';

      if (isMobile) {
        this.list.innerHTML = `
          <div class="fiber-bundle-builder__list-column">
            ${this.parts.map((part) => this.partButtonMarkup(part)).join('')}
          </div>
        `;
        return;
      }

      const groups = [
        this.parts.filter((part) => Number(part.partNumber) >= 1 && Number(part.partNumber) <= 7),
        this.parts.filter((part) => Number(part.partNumber) >= 8 && Number(part.partNumber) <= 14),
        this.parts.filter((part) => Number(part.partNumber) >= 15 && Number(part.partNumber) <= 21),
        this.parts.filter((part) => Number(part.partNumber) >= 22),
      ].filter((group) => group.length);

      this.list.innerHTML = groups
        .map(
          (group) => `
            <div class="fiber-bundle-builder__list-column">
              ${group.map((part) => this.partButtonMarkup(part)).join('')}
            </div>
          `
        )
        .join('');
    }

    getListMode() {
      if (this.mobileMediaQuery) return this.mobileMediaQuery.matches ? 'mobile' : 'desktop';
      return window.innerWidth <= 749 ? 'mobile' : 'desktop';
    }

    partButtonMarkup(part) {
      return `
        <button type="button" class="fiber-bundle-builder__part-button" data-part-id="${this.escapeAttribute(part.id)}">
          <span class="fiber-bundle-builder__number">${this.escapeHtml(part.partNumber)}</span>
          <span>${this.escapeHtml(part.title)}</span>
        </button>
      `;
    }

    async loadSvg() {
      if (!this.diagramWrap || !this.svgContainer) return;

      try {
        const response = await fetch(this.diagramWrap.dataset.svgUrl);
        const svgText = await response.text();
        const documentSvg = new DOMParser().parseFromString(svgText, 'image/svg+xml').querySelector('svg');
        if (!documentSvg) return;

        documentSvg.setAttribute('role', 'img');
        documentSvg.setAttribute('aria-label', this.diagramWrap.dataset.svgLabel || '');
        documentSvg.removeAttribute('width');
        documentSvg.removeAttribute('height');
        this.svgContainer.replaceChildren(documentSvg);
        this.svg = documentSvg;
        this.bindSvgParts();
      } catch (error) {
        this.svgContainer.innerHTML = `<img src="${this.escapeAttribute(this.diagramWrap.dataset.svgUrl)}" alt="${this.escapeAttribute(this.diagramWrap.dataset.svgLabel || '')}" width="5556" height="3773" loading="lazy">`;
      }
    }

    bindSvgParts() {
      if (!this.svg) return;

      this.parts.forEach((part) => {
        const elements = this.findSvgPartElements(part);
        if (!elements.length) return;

        elements.forEach((element) => {
          element.dataset.partId = part.id;
          element.setAttribute('role', 'button');
          element.setAttribute('tabindex', '0');
          element.setAttribute('aria-label', `${part.partNumber} ${part.title}`);

          const label = element.querySelector(`[id="${this.escapeSelector(part.partNumber)}"]`);
          if (label) label.dataset.partNumberLabel = '';
        });
      });

      this.bindSvgHoverSync();
    }

    bindSvgHoverSync() {
      const partElements = this.svg.querySelectorAll('[data-part-id]');
      partElements.forEach((element) => {
        element.addEventListener('mouseenter', () => {
          this.setHoveredState(element.dataset.partId);
        });
        element.addEventListener('mouseleave', () => {
          this.clearHoveredState();
        });
      });
    }

    findSvgPartElements(part) {
      const partNumberId = this.normalizeSvgId(`part-number-${part.partNumber}`);
      const partImageId = this.normalizeSvgId(`part-image-${part.partNumber}`);
      const elements = Array.from(this.svg.querySelectorAll('[id]'));

      return elements.filter((element) => {
        const elementId = this.normalizeSvgId(element.id);
        return elementId === partNumberId || elementId === partImageId;
      });
    }

    normalizeSvgId(value) {
      return String(value || '')
        .replace(/&amp;/g, '&')
        .replace(/\s+/g, ' ')
        .trim()
        .toUpperCase();
    }

    bindEvents() {
      this.root.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-part-id]');
        if (!trigger || !this.root.contains(trigger)) return;
        const part = this.parts.find((item) => String(item.id) === String(trigger.dataset.partId));
        if (part) this.openPart(part);
      });

      this.root.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        const trigger = event.target.closest('[data-part-id]');
        if (!trigger || !this.root.contains(trigger)) return;
        event.preventDefault();
        trigger.dispatchEvent(new MouseEvent('click', { bubbles: true }));
      });

      this.root.addEventListener('mouseover', (event) => {
        const trigger = event.target.closest('[data-part-id]');
        if (!trigger || !this.root.contains(trigger)) return;
        this.setHoveredState(trigger.dataset.partId);
      });

      this.root.addEventListener('mouseout', (event) => {
        const trigger = event.target.closest('[data-part-id]');
        if (!trigger || !this.root.contains(trigger)) return;
        const relatedTrigger = event.relatedTarget?.closest?.('[data-part-id]');
        if (relatedTrigger && this.root.contains(relatedTrigger) && relatedTrigger.dataset.partId === trigger.dataset.partId) return;
        this.clearHoveredState();
      });

      this.root.addEventListener('dblclick', (event) => {
        if (event.target.closest('[data-svg-container], [data-zoom-in], [data-zoom-out], [data-zoom-reset]')) {
          event.preventDefault();
        }
      });

      this.closeButton?.addEventListener('click', () => this.modal.close());
      this.modal?.addEventListener('click', (event) => {
        if (event.target === this.modal) this.modal.close();
      });
      this.modal?.addEventListener('close', () => this.clearActiveState());

      this.root.querySelector('[data-quantity-minus]')?.addEventListener('click', () => this.setQuantity(Number(this.quantity.value || 1) - 1));
      this.root.querySelector('[data-quantity-plus]')?.addEventListener('click', () => this.setQuantity(Number(this.quantity.value || 1) + 1));
      this.quantity?.addEventListener('change', () => this.setQuantity(Number(this.quantity.value || 1)));
      this.addButton?.addEventListener('click', () => this.addToCart());
      this.zoomInButton?.addEventListener('click', () => this.setDiagramZoom(this.diagramZoom + 0.25));
      this.zoomOutButton?.addEventListener('click', () => this.setDiagramZoom(this.diagramZoom - 0.25));
      this.zoomResetButton?.addEventListener('click', () => this.setDiagramZoom(1));
      this.mobileMediaQuery?.addEventListener('change', () => this.handleListBreakpointChange());
    }

    handleListBreakpointChange() {
      const nextListMode = this.getListMode();
      if (nextListMode === this.listMode) return;

      this.listMode = nextListMode;
      this.renderList();
      if (this.activePart) this.setActiveState(this.activePart.id);
      this.clearHoveredState();
      this.setDiagramZoom(1);
    }

    setDiagramZoom(value) {
      const nextZoom = Math.min(2.5, Math.max(1, Number(value) || 1));
      this.diagramZoom = nextZoom;
      this.svgContainer?.style.setProperty('--diagram-zoom', String(nextZoom));

      if (this.zoomResetButton) {
        this.zoomResetButton.textContent = `${Math.round(nextZoom * 100)}%`;
      }

      this.zoomOutButton?.toggleAttribute('disabled', nextZoom <= 1);
      this.zoomInButton?.toggleAttribute('disabled', nextZoom >= 2.5);
    }

    openPart(part) {
      this.activePart = part;
      this.selectedVariant = this.getInitialVariant(part);
      this.clearHoveredState();
      this.setActiveState(part.id);
      this.renderModal(part);
      this.error.hidden = true;
      this.quantity.value = 1;

      // Add helper class to body when modal opens
      document.body.classList.add('modal-open_fiber');

      if (this.modal?.open) return;
      this.modal?.showModal();
    }

    setActiveState(partId) {
      this.root.querySelectorAll('[data-part-id]').forEach((element) => {
        element.classList.toggle('is-active', String(element.dataset.partId) === String(partId));
      });
    }

    clearActiveState() {
      this.activePart = null;
      this.selectedVariant = null;
      this.clearHoveredState();
      this.root.querySelectorAll('[data-part-id].is-active').forEach((element) => {
        element.classList.remove('is-active');
      });
      this.root.querySelector('[data-part-id]:focus')?.blur();

      // Remove helper class from body when modal closes
      document.body.classList.remove('modal-open_fiber');
    }

    setHoveredState(partId) {
      this.clearHoveredState();
      this.root.querySelectorAll(`[data-part-id="${this.escapeSelector(partId)}"]`).forEach((element) => {
        element.classList.add('is-hovered-group');
      });
    }

    clearHoveredState() {
      this.root.querySelectorAll('[data-part-id].is-hovered-group').forEach((element) => {
        element.classList.remove('is-hovered-group');
      });
    }

    renderModal(part) {
      this.root.querySelector('[data-modal-heading]').textContent = part.title;

      this.renderOptions(part.product, true);
      this.updateVariantDetails();
    }

    renderOptions(product, resetSelections = false) {
      const optionsWrap = this.root.querySelector('[data-product-options]');
      if (!optionsWrap) return;

      const options = product.options || [];
      if (!options.length || options.every((option) => option.name === 'Title' && option.values?.[0] === 'Default Title')) {
        optionsWrap.innerHTML = '';
        return;
      }

      // Read current selections from existing DOM selects before re-rendering
      const existingSelects = optionsWrap.querySelectorAll('[data-option-index]');
      const currentSelections = resetSelections ? [] : Array.from(existingSelects).map((select) => select.value);
      if (!currentSelections.length && this.selectedVariant?.options) {
        currentSelections.push(...this.selectedVariant.options);
      }

      optionsWrap.innerHTML = options
        .map((option, index) => {
          const selectedValue = currentSelections[index] || option.values?.[0] || '';
          const availableValues = this.getAvailableOptionValues(index, currentSelections);
          const isFilterable = index > 0;
          return `
            <div class="fiber-bundle-builder__option">
              <label for="FiberBundleOption-${this.escapeAttribute(this.root.dataset.sectionId)}-${index}">${this.escapeHtml(option.name)}</label>
              <select id="FiberBundleOption-${this.escapeAttribute(this.root.dataset.sectionId)}-${index}" data-option-index="${index}"${isFilterable ? ' data-filterable' : ''}>
                ${(option.values || [])
                  .map((value) => {
                    const isUnavailable = !availableValues.includes(String(value));
                    const isSelected = String(value) === String(selectedValue);
                    const label = isUnavailable ? `${value} - Unavailable` : value;
                    return `<option value="${this.escapeAttribute(value)}" ${isSelected ? 'selected' : ''} ${isUnavailable ? 'class="option_inactive"' : ''}>${this.escapeHtml(label)}</option>`;
                  })
                  .join('')}
              </select>
            </div>
          `;
        })
        .join('');

      optionsWrap.querySelectorAll('select').forEach((select) => {
        const index = Number(select.dataset.optionIndex);

        if (index > 0) {
          const filterAvailable = () => {
            const allSelects = optionsWrap.querySelectorAll('[data-option-index]');
            const selections = Array.from(allSelects).map((s) => s.value);
            const avail = this.getAvailableOptionValues(index, selections);
            const currentVal = select.value;
            const allVals = options[index]?.values || [];

            select.innerHTML = allVals
              .filter((v) => avail.includes(String(v)) || String(v) === String(currentVal))
              .map((value) => {
                const isAvail = avail.includes(String(value));
                return `<option value="${this.escapeAttribute(value)}" ${String(value) === String(currentVal) ? 'selected' : ''}>${this.escapeHtml(isAvail ? value : `${value} - Unavailable`)}</option>`;
              })
              .join('');
          };

          select.addEventListener('mousedown', filterAvailable, { once: true });
          select.addEventListener('focus', filterAvailable, { once: true });
        }

        select.addEventListener('change', () => {
          this.renderOptions(product);
          this.selectedVariant = this.findSelectedVariant();
          this.updateVariantDetails();
        });
      });
    }

    getAvailableOptionValues(optionIndex, currentSelections) {
      const product = this.activePart?.product;
      if (!product || !product.variants) return [];

      const valid = new Set();
      product.variants.forEach((variant) => {
        if (!variant.available) return;

        const matches = variant.options.every((value, index) => {
          if (index >= optionIndex) return true;
          return String(value) === String(currentSelections[index]);
        });
        if (matches) {
          valid.add(String(variant.options[optionIndex]));
        }
      });
      return [...valid];
    }

    getInitialVariant(part) {
      return (
        part.product.variants.find((variant) => String(variant.id) === String(part.product.initialVariantId)) ||
        part.product.variants.find((variant) => variant.available) ||
        part.product.variants[0] ||
        null
      );
    }

    findSelectedVariant() {
      const selectedOptions = Array.from(this.root.querySelectorAll('[data-option-index]')).map((select) => select.value);
      return (
        this.activePart.product.variants.find((variant) =>
          selectedOptions.every((value, index) => String(variant.options?.[index]) === String(value))
        ) || null
      );
    }

    updateVariantDetails() {
      const price = this.root.querySelector('[data-product-price]');
      const sku = this.root.querySelector('[data-product-sku]');
      const buttonText = this.addButton?.querySelector('span');
      const hasInactiveOption = Array.from(this.root.querySelectorAll('[data-option-index]')).some((select) => {
        return select.options[select.selectedIndex]?.classList.contains('option_inactive');
      });
      this.updateProductImage();
      this.updateProductInfo();

      if (!this.selectedVariant) {
        price.textContent = '';
        sku.textContent = '';
        this.addButton.disabled = true;
        if (buttonText) {
          buttonText.textContent = window.variantStrings?.unavailableSelectAnother || window.variantStrings?.unavailable || 'Unavailable - select another option';
        }
        return;
      }

      price.textContent = this.formatMoney(this.selectedVariant.price);
      sku.textContent = this.selectedVariant.sku || this.activePart.partNumber || '';

      if (hasInactiveOption) {
        this.addButton.disabled = true;
        if (buttonText) {
          buttonText.textContent = window.variantStrings?.unavailableSelectAnother || window.variantStrings?.unavailable || 'Unavailable - select another option';
        }
        return;
      }

      this.addButton.disabled = !this.selectedVariant.available;
      if (buttonText) {
        buttonText.textContent = this.selectedVariant.available
          ? window.variantStrings?.addToCart || 'Add to cart'
          : window.variantStrings?.soldOut || 'Sold out';
      }
    }

    updateProductInfo() {
      const productTitleLink = this.root.querySelector('[data-product-title]');
      const productLink = this.root.querySelector('[data-product-link]');
      const description = this.root.querySelector('[data-product-description]');
      const variant = this.selectedVariant;
      const product = this.activePart?.product;

      const title = variant?.productTitle || product?.title || '';
      const desc = variant?.productDescription || product?.description || '';
      const url = variant?.productUrl || product?.url || '#';

      if (productTitleLink && productLink) {
        productTitleLink.textContent = title;
        productLink.href = url;
        productLink.setAttribute('aria-label', `${title} product page`);
      }

      if (description) {
        description.innerHTML = desc;
      }
    }

    updateProductImage() {
      const image = this.root.querySelector('[data-product-image]');
      if (!image || !this.activePart?.product) return;

      const product = this.activePart.product;
      const v = this.selectedVariant;
      const imageUrl = v?.featuredImage || v?.productFeaturedImage || product.featuredImage;
      const imageAlt = v?.featuredImageAlt || v?.productFeaturedImageAlt || product.featuredImageAlt || product.title;

      if (imageUrl) {
                image.src = imageUrl;
        image.alt = imageAlt;
        image.hidden = false;
      } else {
        image.removeAttribute('src');
        image.alt = '';
        image.hidden = true;
      }
    }

    getProductUrl(product) {
      if (!product.url) return '#';
      if (!product.initialVariantId) return product.url;

      const separator = product.url.includes('?') ? '&' : '?';
      return `${product.url}${separator}variant=${encodeURIComponent(product.initialVariantId)}`;
    }

    setQuantity(value) {
      this.quantity.value = Math.max(1, Number.isNaN(value) ? 1 : value);
    }

    async addToCart() {
      if (!this.selectedVariant || !this.selectedVariant.available) return;
      if (this.addButton.getAttribute('aria-disabled') === 'true') return;

      this.error.hidden = true;
      this.addButton.classList.add('loading');
      this.spinner?.classList.remove('hidden');
      this.addButton.setAttribute('aria-disabled', true);

      const formData = new FormData();
      formData.append('id', this.selectedVariant.id);
      formData.append('quantity', this.quantity.value || 1);
      formData.append('properties[Part Number]', this.selectedVariant.sku || '');

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cart) {
        formData.append(
          'sections',
          cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement(document.activeElement);
      }

      const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: {} };
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      try {
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', config);
        const parsedState = await response.json();

        if (parsedState.status) {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'fiber-bundle-builder',
              productVariantId: formData.get('id'),
              errors: parsedState.errors || parsedState.description,
              message: parsedState.message,
            });
          }
          this.showError(parsedState.description || parsedState.message);
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'fiber-bundle-builder',
            productVariantId: formData.get('id'),
            cartData: parsedState,
          });
        }

        if (cart && parsedState.sections) {
          cart.renderContents(parsedState);
        } else {
          window.location = window.routes?.cart_url || '/cart';
        }

        this.modal.close();
      } catch (error) {
        this.showError(window.cartStrings?.error || 'There was an error while adding this item to cart.');
      } finally {
        this.addButton.classList.remove('loading');
        if (cart?.classList.contains('is-empty')) cart.classList.remove('is-empty');
        this.spinner?.classList.add('hidden');
        this.addButton.removeAttribute('aria-disabled');
      }
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = false;
    }

    formatMoney(value) {
      const amount = Number(value || 0) / 100;
      const currency = window.Shopify?.currency?.active || 'USD';

      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency,
        }).format(amount);
      } catch (error) {
        return `$${amount.toFixed(2)}`;
      }
    }

    escapeHtml(value) {
      return String(value ?? '').replace(/[&<>'"]/g, (character) => {
        const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' };
        return map[character];
      });
    }

    escapeAttribute(value) {
      return this.escapeHtml(value);
    }

    escapeSelector(value) {
      return String(value ?? '').replace(/[\\"]/g, '\\$&');
    }
  }

  const initFiberCableTrayBundleBuilders = (container = document) => {
    container.querySelectorAll('.fiber-bundle-builder').forEach((root) => {
      if (!root.dataset.initialized) {
        root.dataset.initialized = 'true';
        new FiberCableTrayBundleBuilder(root);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initFiberCableTrayBundleBuilders());
  } else {
    initFiberCableTrayBundleBuilders();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initFiberCableTrayBundleBuilders(event.target);
  });
})();
